import { useParams, Navigate } from 'react-router-dom';
import { useSEO, generateBreadcrumbSchema } from '../../../utils/seo';
import { localDeals } from '../../../mocks/deals';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import DealHero from './components/DealHero';
import DealInfo from './components/DealInfo';
import BusinessCard from './components/BusinessCard';
import DealMap from './components/DealMap';
import DealTerms from './components/DealTerms';
import SimilarDeals from './components/SimilarDeals';
import FavoriteButton from '../../../components/FavoriteButton';

export default function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const deal = localDeals.find((d) => d.id === Number(id));

  // Generate schemas
  const schemas = deal ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Offer',
        name: deal.dealTitle,
        description: deal.description,
        url: `https://roseville.org/deals/${id}`,
        price: deal.discount.includes('%') ? '0' : deal.discount.replace(/[^0-9.]/g, ''),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        validThrough: deal.expiresAt,
        seller: {
          '@type': 'LocalBusiness',
          name: deal.businessName,
          address: {
            '@type': 'PostalAddress',
            streetAddress: deal.address.split(',')[0],
            addressLocality: 'Roseville',
            addressRegion: 'CA',
            postalCode: '95678',
            addressCountry: 'US',
          },
          telephone: deal.phone,
        },
        itemOffered: {
          '@type': 'Service',
          name: deal.dealTitle,
          description: deal.description,
        },
      },
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Deals', url: '/deals' },
        { name: deal.dealTitle, url: `/deals/${id}` }
      ])
    ]
  } : undefined;

  useSEO({
    title: deal
      ? `${deal.dealTitle} — ${deal.businessName} | Roseville Deals`
      : 'Deal Not Found | Roseville.org',
    description: deal
      ? `${deal.description.slice(0, 155)}...`
      : 'This deal could not be found.',
    keywords: deal
      ? `${deal.businessName}, ${deal.dealTitle}, Roseville deals, Roseville coupons, ${deal.category}, discount, savings`
      : 'Roseville deals',
    canonical: `/deals/${id}`,
    ogType: 'product',
    ogImage: deal?.image,
    ogImageAlt: deal ? `${deal.dealTitle} at ${deal.businessName}` : undefined,
    schema: schemas,
  });

  if (!deal) {
    return <Navigate to="/deals" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-20">
        <DealHero deal={deal} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-8">
              <div className="flex items-center gap-4">
                <FavoriteButton
                  itemType="deal"
                  itemId={deal.id}
                  itemTitle={deal.title}
                  itemImage={deal.image}
                  itemData={{ business: deal.business, discount: deal.discount }}
                  showLabel={true}
                  className="hover:text-red-500"
                />
              </div>

              <DealInfo deal={deal} />
              <DealMap address={deal.address} businessName={deal.businessName} />
              <DealTerms terms={deal.terms} expiresAt={deal.expiresAt} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <BusinessCard deal={deal} />

              {/* Share card */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">
                  Share This Deal
                </h3>
                <div className="flex items-center gap-3">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      window.location.href
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="w-10 h-10 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-gray-600 hover:text-teal-600 text-lg"></i>
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                      `Check out this deal: ${deal.dealTitle} at ${deal.businessName}!`
                    )}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="w-10 h-10 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-twitter-x-fill text-gray-600 hover:text-teal-600 text-lg"></i>
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(
                      `Great deal at ${deal.businessName}`
                    )}&body=${encodeURIComponent(
                      `Check out this deal: ${deal.dealTitle}\n\n${deal.description}\n\nCoupon code: ${deal.couponCode}`
                    )}`}
                    className="w-10 h-10 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-mail-line text-gray-600 hover:text-teal-600 text-lg"></i>
                  </a>
                  <button
                    onClick={() => {
                      // Gracefully handle clipboard errors
                      navigator.clipboard
                        .writeText(window.location.href)
                        .catch((err) => {
                          console.error('Failed to copy URL:', err);
                        });
                    }}
                    className="w-10 h-10 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-link text-gray-600 hover:text-teal-600 text-lg"></i>
                  </button>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-teal-700 to-teal-800 rounded-xl p-6 text-white">
                <h3 className="text-base font-bold mb-2">
                  Own a Local Business?
                </h3>
                <p className="text-sm text-white/80 mb-4 leading-relaxed">
                  Reach thousands of Roseville residents with your own deal
                  listing.
                </p>
                <a
                  href="/advertise"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-teal-700 text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-megaphone-line text-sm"></i>
                  Advertise With Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SimilarDeals currentDealId={deal.id} category={deal.category} />
      <Footer />
    </div>
  );
}